import React from 'react';
import style from './style.module.scss';
import { TranslationMap, SupportedLanguages } from '../../types';
import { ErrorAwareLabel } from '../error-aware-label';
import { RichText } from '../rich-text';
import { Card, Switch } from 'antd';
import { Button } from '../button';

interface CheckboxFormErrors {
    fr: boolean;
    en: boolean;
}

export interface CheckboxItemError {
    fr: boolean;
    en: boolean;
    message?: string;
}

export interface Checkboxes {
    id: number | null;
    isMandatory: boolean | null;
    deletedAt?: string | null;
    translations: TranslationMap;
}

interface CheckboxFormValues {
    checkboxes: Checkboxes[];
}
interface CheckboxFormProps {
    values: CheckboxFormValues;
    errors: CheckboxFormErrors;
    onChange: (values: CheckboxFormValues) => void;
    /**
     * Per-item errors, indexed by the position of each non-deleted checkbox
     * as rendered (i.e. matching `checkboxes.filter(cb => !cb.deletedAt)`).
     * When a new non-empty error set is provided, the first erroneous card is
     * automatically scrolled into view.
     */
    itemErrors?: CheckboxItemError[];
}

export const CheckboxForm = (props: CheckboxFormProps) => {
    const [checkboxesList, setcheckboxesList] = React.useState(
        props.values.checkboxes
    );
    const cardRefs = React.useRef<(HTMLDivElement | null)[]>([]);

    React.useEffect(() => {
        setcheckboxesList(props.values.checkboxes);
    }, [props.values.checkboxes]);

    React.useEffect(() => {
        if (!props.itemErrors) return;
        const firstErrorIdx = props.itemErrors.findIndex(
            (err) => err && (err.fr || err.en)
        );
        if (firstErrorIdx >= 0) {
            const node = cardRefs.current[firstErrorIdx];
            if (node) {
                node.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                });
            }
        }
    }, [props.itemErrors]);

    const removeCheckbox = async (checkbox: Checkboxes) => {
        checkbox.deletedAt = new Date().toISOString();

        setcheckboxesList([...checkboxesList]);
    };

    const setMandatory = async (checkbox: Checkboxes, check: boolean) => {
        checkbox.isMandatory = check;

        setcheckboxesList([...checkboxesList]);
    };

    const addCheckbox = async () => {
        setcheckboxesList([
            ...checkboxesList,
            {
                id: null,
                isMandatory: false,
                translations: {
                    fr: '',
                    en: '',
                },
            },
        ]);
        props.values.checkboxes = [
            ...checkboxesList,
            {
                id: null,
                isMandatory: false,
                translations: {
                    fr: '',
                    en: '',
                },
            },
        ];
    };
    const visibleCheckboxes = checkboxesList.filter(
        (checkboxes) => !checkboxes.deletedAt
    );
    return (
        <>
            {/* Checkboxes */}
            <div className={style.checkboxes_container}>
                {visibleCheckboxes.map((checkbox, index) => {
                    const itemError = props.itemErrors?.[index];
                    const hasItemError = Boolean(
                        itemError && (itemError.fr || itemError.en)
                    );
                    return (
                        <div
                            key={index}
                            ref={(el) => (cardRefs.current[index] = el)}
                            className={
                                hasItemError ? style.card_has_error : undefined
                            }
                        >
                            <Card title={`Checkbox ${index + 1}`}>
                                <div className={style.input_row}>
                                    {SupportedLanguages.map((lang) => (
                                        <div
                                            key={lang}
                                            className={
                                                style.fixed_width_input_col
                                            }
                                        >
                                            <ErrorAwareLabel
                                                text={
                                                    lang === 'en'
                                                        ? 'English Text'
                                                        : 'French Text'
                                                }
                                                hasError={
                                                    props.errors[lang] ||
                                                    Boolean(itemError?.[lang])
                                                }
                                            />
                                            <RichText
                                                text={
                                                    checkbox.translations[
                                                        lang
                                                    ] ?? ''
                                                }
                                                onChange={(text) => {
                                                    checkbox.translations[
                                                        lang
                                                    ] = text;
                                                }}
                                            />
                                        </div>
                                    ))}
                                </div>
                                {hasItemError && itemError?.message && (
                                    <div className={style.item_error_message}>
                                        {itemError.message}
                                    </div>
                                )}
                                <div className={style.input_row}>
                                    <span className="left_label">
                                        Is Mandatory
                                    </span>
                                    <Switch
                                        checked={checkbox.isMandatory === true}
                                        onChange={(e) =>
                                            setMandatory(checkbox, e)
                                        }
                                    />
                                </div>
                                <div className={style.input_row}>
                                    <Button
                                        variant="secondary-destructive"
                                        onClick={() => removeCheckbox(checkbox)}
                                    >
                                        Delete checkbox {index + 1}
                                    </Button>
                                </div>
                            </Card>
                        </div>
                    );
                })}
            </div>
            <div className={style.input_row}>
                <Button variant="secondary" onClick={() => addCheckbox()}>
                    Add checkbox
                </Button>
            </div>
        </>
    );
};
